import http from 'hub-http/clients/apiClient';
import { createRpcClientV2 } from 'rpc-client-utils';
import { useQuery, registerQuery } from 'data-fetching-client';
import { useEffect, useMemo, useCallback, useState } from 'react';
import { transformViewWithPermissionsToInternalFormat } from '../dataOperators/composableSearchViews';
import Raven from 'raven-js';
import { usePagingState } from './usePagingState';
import { sanitizeQuery } from '../utils/sanitizeQuery';
import { useObjectTypeIdsInNamespaces } from '../../objectDefinitions/hooks/useObjectTypeIdsInNamespaces';
import { useHasBulkActionsGate } from '../../userInfo/hooks/useHasBulkActionsGate';
import { getViewsByComposableSearchRpc } from '../../../__generated__/chirp/com/hubspot/sales/rpc/ViewsCrudRpc';
import { buildComposableSearchRequest, COMPOSABLE_SEARCH_TIMEOUT_MS, DEFAULT_PAGE_SIZE } from '../utils/composableSearchRequest';

// Re-exported so existing importers keep working; both now live in a leaf
// module that the early requester can also import. See that file for why.
export { DEFAULT_PAGE_SIZE, COMPOSABLE_SEARCH_TIMEOUT_MS };
export const VIEWS_SEARCH_QUERY_NAME = 'viewsSearch';
const chirpClient = createRpcClientV2({
  hubHttpClient: http
});
const fetchComposableSearch = query => chirpClient.call(getViewsByComposableSearchRpc, {
  timeout: COMPOSABLE_SEARCH_TIMEOUT_MS,
  request: buildComposableSearchRequest(query)
});
export const transformComposableSearchResponse = response => ({
  total: response.total,
  results: (response.results || []).map(transformViewWithPermissionsToInternalFormat),
  // CHIRP paginates with {hasMore, offset}; adapt to the {paging:{next:{after}}}
  // contract that usePagingState already understands.
  paging: response.hasMore && response.offset ? {
    next: {
      after: response.offset
    }
  } : undefined
});
export const ViewsSearchQuery = registerQuery({
  fieldName: VIEWS_SEARCH_QUERY_NAME,
  args: ['viewName', 'ownerIds', 'teamIds', 'objectTypeIds', 'private', 'limit', 'pagingToken', 'sortColumn', 'sortOrder', 'namespace', 'includeLastUsed'],
  fetcher: variables => fetchComposableSearch(variables).then(transformComposableSearchResponse)
});
export const useFetchViewsSearch = externalSearchQueryObject => {
  const {
    currentPagingToken,
    addPagingToken,
    resetPaginationState,
    goToPreviousPage: paginationGoToPreviousPage,
    goToNextPage: paginationGoToNextPage,
    hasNextPage,
    hasPreviousPage,
    previousPagingToken,
    nextPagingToken
  } = usePagingState();
  const [internalQueryObject, setQueryObject] = useState(null);
  const namespaces = useMemo(() => [externalSearchQueryObject.namespace], [externalSearchQueryObject.namespace]);
  const {
    data: defaultObjectTypeIds,
    loading: defaultObjectTypeIdsLoading
  } = useObjectTypeIdsInNamespaces({
    namespaces
  });
  const sanitizedQuery = useMemo(() => sanitizeQuery(externalSearchQueryObject, defaultObjectTypeIds || []), [externalSearchQueryObject, defaultObjectTypeIds]);
  const includeLastUsed = useHasBulkActionsGate();
  const queryResult = useQuery(ViewsSearchQuery, {
    skip: internalQueryObject === null,
    variables: Object.assign({}, internalQueryObject, {
      pagingToken: currentPagingToken,
      includeLastUsed
    }),
    onError: e => {
      Raven.captureException(e, {
        extra: {
          sanitizedQuery,
          internalQueryObject,
          currentPagingToken
        }
      });
    }
    // notifyOnNetworkStatusChange: true, //this will update queryResult.loading whenever we page through
  });
  useEffect(() => {
    if (defaultObjectTypeIdsLoading) {
      return;
    }
    setQueryObject(sanitizedQuery);
    resetPaginationState();
  }, [defaultObjectTypeIdsLoading, sanitizedQuery, resetPaginationState]);

  /**
   * This useEffect looks through the new queryResult and determines if
   * - did the user go to the next page and unconver a new paging token
   *   If so, it appends the new paging token to the array
   * - did the user go to the next page and realize they are at the last page
   *   If so, it appends END_OF_PAGING to the paging tokens array
   **/
  useEffect(() => {
    if (!queryResult.data) {
      return;
    }
    const newPagingToken = queryResult.data.viewsSearch.paging && queryResult.data.viewsSearch.paging.next.after;
    addPagingToken(newPagingToken);
  }, [addPagingToken, queryResult.data]);
  const goToPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      paginationGoToPreviousPage();
      setQueryObject(Object.assign({}, sanitizedQuery, {
        pagingToken: previousPagingToken
      }));
    }
  }, [sanitizedQuery, hasPreviousPage, paginationGoToPreviousPage, previousPagingToken]);
  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      paginationGoToNextPage();
      setQueryObject(Object.assign({}, sanitizedQuery, {
        pagingToken: nextPagingToken
      }));
    }
  }, [hasNextPage, sanitizedQuery, paginationGoToNextPage, nextPagingToken]);
  return useMemo(() => Object.assign({}, queryResult, {
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage
  }), [queryResult, goToNextPage, hasNextPage, goToPreviousPage, hasPreviousPage]);
};